import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { useState } from "react";
import {
  isManagementUser,
  isReadOnlyUser,
} from "../../../../shared/utility";
import ReceivingSubTable2 from "./ReceivingSubtable2";
import NumberInputText from "../../../controls/inputs/NumberInputText";
import { useCellSaveState } from "../../../../hooks/useCellSaveState";

export default function ReceivingSubTable1(props) {
  const {
    data,
    loadingRows,
    rowDetails,
    fetchSubRowDetails,
    subRowDetails,
    subLoadingRows,
    selectedItems,
    setSelectedItems,
    onUpdateReceivingItem,
    closed,
  } = props;

  const [expandedRows, setExpandedRows] = useState(null);
  const { getSaveStatus, saveCell } = useCellSaveState();
  const isManagement = isManagementUser();
  const isReadOnly = isReadOnlyUser();

  const isCartonRow = (rowData) => rowData.id && !rowData.pallet_id;
  const canEditRow = (rowData) =>
    isManagement &&
    !isReadOnly &&
    !closed &&
    isCartonRow(rowData) &&
    typeof onUpdateReceivingItem === "function";

  const cartonCellKey = (item, field) => `carton:${item.id}:${field}`;

  const commitReceivingItem = (item, field, value) => {
    return saveCell(cartonCellKey(item, field), () =>
      onUpdateReceivingItem(
        {
          id: item.id,
          [field]: value,
        },
        data,
      ),
    );
  };

  const onRowExpand = (event) => {
    const rowData = event.data;
    if (!subRowDetails[rowData.unique_id]) {
      fetchSubRowDetails(rowData);
    }
  };

  const allowExpansion = (rowData) => {
    return rowData.pallet_id;
  };

  function rowExpansionTemplate(data) {
    return (
      <ReceivingSubTable2
        data={data}
        loadingRows={subLoadingRows}
        rowDetails={subRowDetails}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
      />
    );
  }

  const bodyProductName = (item) => {
    return (
      <div>
        {item.product_name} <br />{" "}
        <span className="font-bold">PLU: {item.plu}</span>
      </div>
    );
  };

  if (loadingRows[data.unique_id]) {
    return <ProgressSpinner style={{ width: "30px", height: "30px" }} />;
  }

  const detail = rowDetails[data.unique_id];
  if (!detail) return null;

  const barcodeBody = (data) => {
    return <p>{data.barcode}</p>;
  };

  const bodySubItemWeight = (item) => {
    const editable = canEditRow(item);

    return (
      <div className="space-y-1">
        <NumberInputText
          value={item.total_weight_in_kg}
          aria-label="Total weight in kilograms"
          className="editable-table-number"
          inputClassName="editable-table-input"
          disabled={!editable}
          saveStatus={getSaveStatus(
            cartonCellKey(item, "total_weight_in_kg"),
          )}
          onCommit={(value) =>
            commitReceivingItem(item, "total_weight_in_kg", value)
          }
          minFractionDigits={2}
          maxFractionDigits={2}
          min={0}
          suffix=" kg"
        />
        <NumberInputText
          value={item.total_weight_in_lbs}
          aria-label="Total weight in pounds"
          className="editable-table-number"
          inputClassName="editable-table-input"
          disabled={!editable}
          saveStatus={getSaveStatus(
            cartonCellKey(item, "total_weight_in_lbs"),
          )}
          onCommit={(value) =>
            commitReceivingItem(item, "total_weight_in_lbs", value)
          }
          minFractionDigits={2}
          maxFractionDigits={2}
          min={0}
          suffix=" lbs"
        />
      </div>
    );
  };

  const bodyItemPieceCount = (item) => {
    return (
      <NumberInputText
        value={item.total_pieces}
        aria-label="Piece count"
        className="editable-table-number"
        inputClassName="editable-table-input"
        disabled={!canEditRow(item)}
        saveStatus={getSaveStatus(cartonCellKey(item, "total_pieces"))}
        onCommit={(value) =>
          commitReceivingItem(item, "total_pieces", value)
        }
        minFractionDigits={0}
        maxFractionDigits={0}
        min={0}
        suffix=" pieces"
      />
    );
  };

  const bodyParentUnitCost = (item) => {
    return (
      <NumberInputText
        value={item.unit_cost}
        aria-label="Unit cost"
        className="editable-table-number"
        inputClassName="editable-table-input"
        disabled={!canEditRow(item)}
        saveStatus={getSaveStatus(cartonCellKey(item, "unit_cost"))}
        onCommit={(value) => commitReceivingItem(item, "unit_cost", value)}
        mode="currency"
        currency="USD"
        locale="en-US"
        minFractionDigits={2}
        maxFractionDigits={2}
        min={0}
      />
    );
  };

  const bodyParentTotalCost = (item) => {
    return (
      <NumberInputText
        value={item.total_cost}
        aria-label="Total cost"
        className="editable-table-number"
        inputClassName="editable-table-input"
        disabled={!canEditRow(item)}
        saveStatus={getSaveStatus(cartonCellKey(item, "total_cost"))}
        onCommit={(value) => commitReceivingItem(item, "total_cost", value)}
        mode="currency"
        currency="USD"
        locale="en-US"
        minFractionDigits={2}
        maxFractionDigits={2}
      />
    );
  };

  const bodyParentLocation = (item) => {
    return (
      <p>
        {item.warehouse_name} <br />
        {item.warehouse_location_name}
      </p>
    );
  };

  const cartonCountBody = (item) => {
    return (
      <NumberInputText
        value={item.total_cartons}
        aria-label="Carton count"
        className="editable-table-number"
        inputClassName="editable-table-input"
        disabled
        minFractionDigits={0}
        maxFractionDigits={0}
        suffix=" cartons"
      />
    );
  };

  return (
    <div>
      <DataTable
        value={rowDetails[data.unique_id]}
        expandedRows={expandedRows}
        onRowToggle={(e) => {
          setExpandedRows(e.data);
        }}
        onRowExpand={onRowExpand}
        rowExpansionTemplate={rowExpansionTemplate}
        dataKey="unique_id"
        tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm receiving-subtable-1"
        cellClassName="py-2 bg-gray-100 border-1-bottom-gray px-2 text-sm inv-sub-item"
        selectionMode="checkbox"
        selection={selectedItems}
        onSelectionChange={(e) => setSelectedItems(e.value)}
      >
        <Column
          selectionMode="multiple"
          headerStyle={{ width: "3rem" }}
        ></Column>
        <Column
          header="Product Name"
          body={bodyProductName}
          style={{ width: "17%" }}
        />
        <Column
          field="lot_number"
          header="Lot Number"
          style={{ width: "10%" }}
        />
        <Column header="Barcode" body={barcodeBody} style={{ width: "24%" }} />
        <Column
          header="Carton Count"
          body={cartonCountBody}
          alignHeader="right"
          align="right"
          style={{ width: "8%" }}
        />
        <Column
          header="Total Weight"
          field="total_weight_in_kg"
          body={bodySubItemWeight}
          alignHeader="right"
          align="right"
          style={{ width: "10%" }}
        />
        <Column
          header="Piece Count"
          field="total_pieces"
          body={bodyItemPieceCount}
          alignHeader="right"
          align="right"
          style={{ width: "8%" }}
        />
        {isManagement ? (
          <Column
            header="Unit Cost"
            field="unit_cost"
            body={bodyParentUnitCost}
            alignHeader="right"
            align="right"
            style={{ width: "7%" }}
          />
        ) : null}
        {isManagement ? (
          <Column
            header="Total Cost"
            body={bodyParentTotalCost}
            alignHeader="right"
            align="right"
            style={{ width: "7%" }}
          />
        ) : null}
        <Column
          body={bodyParentLocation}
          header="Location"
          style={{ width: "10%" }}
        />
        <Column expander={allowExpansion} style={{ width: "6%" }} />
      </DataTable>
    </div>
  );
}
